import React from 'react';
import {
  Package,
  Truck,
  Clock,
  MapPin,
  CheckCircle,
  RotateCcw,
  AlertTriangle,
  XCircle,
  Navigation,
  Loader2,
} from 'lucide-react';
import { Button, Badge } from './ui';

// Format date pour la timeline
const formatTimelineDate = (date) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(date);
  }
};

const getStatusConfig = (status) => {
  const configs = {
    created: { label: 'Créé', icon: Package },
    picked_up: { label: 'Récupéré', icon: Truck },
    in_transit: { label: 'En transit', icon: Navigation },
    out_for_delivery: { label: 'En livraison', icon: Truck },
    ready_for_pickup: { label: 'Prêt au retrait', icon: Package },
    delivered: { label: 'Livré', icon: CheckCircle },
    failed: { label: 'Échec livraison', icon: AlertTriangle },
    returned: { label: 'Retourné', icon: RotateCcw },
  };
  return configs[status] || configs.created;
};

const mapStatusToUi = (status) => {
  const s = (status || '').toString().toLowerCase();
  const mapping = {
    pending: 'created',
    received: 'picked_up',
    in_transit: 'in_transit',
    out_for_delivery: 'out_for_delivery',
    ready_for_pickup: 'ready_for_pickup',
    delivered: 'delivered',
    returned: 'returned',
    lost: 'failed',
    damaged: 'failed',
  };
  return mapping[s] || 'created';
};

// Transformer les événements API en format timeline (aligné RelayPackages)
const transformApiEventsToTimeline = (apiEvents) => {
  if (!apiEvents || !Array.isArray(apiEvents)) return [];

  const eventTypeToLabel = {
    DEPART: 'Colis Expédié',
    RECEPTION: 'Colis En Transit',
  };
  const eventTypeToStatus = {
    CREATION: 'created',
    DEPART: 'in_transit',
    RECEPTION: 'in_transit',
    ARRIVEE_DESTINATION: 'ready_for_pickup',
    RETRAIT: 'delivered',
    RETOUR: 'returned',
    RETOURNE: 'returned',
    PERTE: 'failed',
    PERDUE: 'failed',
    ANNULATION: 'failed',
    RETURN_DEPART: 'in_transit',
    RETURN_RECEPTION: 'in_transit',
    RETURN_ARRIVEE: 'returned',
    RETURN_REQUEST: 'returned',
    COLIS_LIVRE_AU_DESTINATAIRE: 'delivered',
    ECHEC_DE_LIVRAISON: 'failed',
    RETROUVE: 'in_transit',
    EN_LIVRAISON: 'out_for_delivery',
  };

  const getEventConfig = (eventType) => {
    const configs = {
      CREATION: { icon: Package, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30', borderColor: 'border-blue-500' },
      DEPART: { icon: Truck, color: 'orange', bgColor: 'bg-orange-100 dark:bg-orange-900/30', borderColor: 'border-orange-500' },
      RECEPTION: { icon: Package, color: 'purple', bgColor: 'bg-purple-100 dark:bg-purple-900/30', borderColor: 'border-purple-500' },
      ARRIVEE_DESTINATION: { icon: CheckCircle, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30', borderColor: 'border-green-500' },
      RETRAIT: { icon: CheckCircle, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30', borderColor: 'border-green-500' },
      RETOUR: { icon: RotateCcw, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
      RETOURNE: { icon: RotateCcw, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
      RETURN_DEPART: { icon: RotateCcw, color: 'orange', bgColor: 'bg-orange-100 dark:bg-orange-900/30', borderColor: 'border-orange-500' },
      RETURN_RECEPTION: { icon: Package, color: 'purple', bgColor: 'bg-purple-100 dark:bg-purple-900/30', borderColor: 'border-purple-500' },
      RETURN_ARRIVEE: { icon: RotateCcw, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
      RETURN_REQUEST: { icon: RotateCcw, color: 'yellow', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', borderColor: 'border-yellow-500' },
      COLIS_LIVRE_AU_DESTINATAIRE: { icon: CheckCircle, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30', borderColor: 'border-green-500' },
      ECHEC_DE_LIVRAISON: { icon: AlertTriangle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
      PERTE: { icon: AlertTriangle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
      PERDUE: { icon: AlertTriangle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
      ANNULATION: { icon: XCircle, color: 'gray', bgColor: 'bg-gray-100 dark:bg-gray-800', borderColor: 'border-gray-500' },
      RETROUVE: { icon: CheckCircle, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30', borderColor: 'border-green-500' },
      EN_LIVRAISON: { icon: Truck, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30', borderColor: 'border-blue-500' },
    };
    return configs[eventType] || { icon: Package, color: 'gray', bgColor: 'bg-gray-100 dark:bg-gray-800', borderColor: 'border-gray-500' };
  };

  return apiEvents
    .slice()
    .reverse()
    .map((event) => {
      const status = eventTypeToStatus[event.event_type] || 'created';
      const config = getEventConfig(event.event_type);
      let location = '';
      if (event.relay) {
        location = event.relay.nom || '';
        if (event.relay.ville) location += `, ${event.relay.ville}`;
      } else if (event.from_relay && event.to_relay) {
        location = `De ${event.from_relay.nom || ''} vers ${event.to_relay.nom || ''}`;
      } else if (event.from_relay) {
        location = `Depuis ${event.from_relay.nom || ''}`;
      } else if (event.to_relay) {
        location = `Vers ${event.to_relay.nom || ''}`;
      }
      const details = [];
      if (event.bordereau_number) details.push(`Bordereau: ${event.bordereau_number}`);
      if (event.driver_name) details.push(`Chauffeur: ${event.driver_name}`);
      if (event.recipient_name) details.push(`Destinataire: ${event.recipient_name}`);
      if (event.actor_type) details.push(`Par: ${event.actor_type}`);
      return {
        id: event.id,
        date: event.created_at,
        status,
        description: event.description || event.event_type_display || event.event_type,
        location,
        details,
        eventType: event.event_type,
        eventTypeLabel: eventTypeToLabel[event.event_type] || event.event_type_display || (event.event_type && event.event_type.replace(/_/g, ' ')) || event.event_type,
        config,
      };
    });
};

// Fallback : générer des événements estimés (comme RelayPackages)
const generateTimelineEvents = (status, dateCreation, dateRetrait, createdByOtherUser = false, creatorName = '') => {
  const events = [
    {
      date: dateCreation,
      status: 'created',
      description: (status === 'created' && createdByOtherUser) ? '⏳ En attente de réception' : '🆕 Expédition créée',
      location: creatorName || '—',
      config: { icon: Package, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30', borderColor: 'border-blue-500' },
    },
  ];
  const createdTime = new Date(dateCreation).getTime();
  const pickupTime = createdTime + 2 * 60 * 60 * 1000;
  const transitTime = createdTime + 6 * 60 * 60 * 1000;
  const outForDeliveryTime = createdTime + 24 * 60 * 60 * 1000;
  const deliveredTime = dateRetrait ? new Date(dateRetrait).getTime() : createdTime + 2 * 24 * 60 * 60 * 1000;
  if (['picked_up', 'in_transit', 'out_for_delivery', 'delivered', 'ready_for_pickup'].includes(status)) {
    events.push({
      date: new Date(pickupTime).toISOString(),
      status: 'picked_up',
      description: '📦 Colis récupéré',
      location: 'Centre de tri',
      config: { icon: Truck, color: 'orange', bgColor: 'bg-orange-100 dark:bg-orange-900/30', borderColor: 'border-orange-500' },
    });
  }
  if (['in_transit', 'out_for_delivery', 'delivered'].includes(status)) {
    events.push({
      date: new Date(transitTime).toISOString(),
      status: 'in_transit',
      description: '🚚 En transit',
      location: 'En route',
      config: { icon: Navigation, color: 'purple', bgColor: 'bg-purple-100 dark:bg-purple-900/30', borderColor: 'border-purple-500' },
    });
  }
  if (['out_for_delivery', 'delivered'].includes(status)) {
    events.push({
      date: new Date(outForDeliveryTime).toISOString(),
      status: 'out_for_delivery',
      description: '🏃 En cours de livraison',
      location: 'Véhicule de livraison',
      config: { icon: Truck, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30', borderColor: 'border-blue-500' },
    });
  }
  if (status === 'delivered') {
    events.push({
      date: new Date(deliveredTime).toISOString(),
      status: 'delivered',
      description: '✅ Livré',
      location: 'Destinataire',
      config: { icon: CheckCircle, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30', borderColor: 'border-green-500' },
    });
  }
  if (status === 'ready_for_pickup') {
    events.push({
      date: dateRetrait || new Date().toISOString(),
      status: 'ready_for_pickup',
      description: '📬 Prêt au retrait',
      location: 'Point Relais',
      config: { icon: Package, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30', borderColor: 'border-green-500' },
    });
  }
  if (status === 'failed' || status === 'returned') {
    events.push({
      date: new Date(outForDeliveryTime).toISOString(),
      status,
      description: status === 'failed' ? '❌ Échec de livraison' : '🔙 Retourné',
      location: status === 'failed' ? 'Centre de tri' : 'Retour expéditeur',
      config: { icon: status === 'failed' ? AlertTriangle : RotateCcw, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30', borderColor: 'border-red-500' },
    });
  }
  return events;
};

// Libellé du statut current_status pour affichage
const getCurrentStatusLabel = (currentStatus) => {
  if (!currentStatus) return '—';
  const n = (currentStatus || '').toString().toLowerCase().replace(/_/g, ' ');
  const map = {
    a_expedier: 'À expédier',
    expedie: 'Expédié',
    en_transit: 'En transit',
    en_attente_retrait: 'En attente de retrait',
    retire: 'Retiré',
    livre: 'Livré',
    retourne: 'Retourné',
    perdue: 'Perdue',
    annule: 'Annulé',
  };
  return map[n] || currentStatus;
};

// Composant Timeline (aligné RelayPackages)
const TrackingTimeline = ({ events, isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Chargement de l'historique...</span>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>Aucun événement de traçabilité disponible</p>
      </div>
    );
  }

  const getColorClasses = (color) => {
    const colorMap = {
      blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', border: 'border-blue-500', text: 'text-blue-600 dark:text-blue-400', textDark: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-500', gradient: 'from-blue-300 to-blue-200 dark:from-blue-700 dark:to-blue-600' },
      green: { bg: 'bg-green-100 dark:bg-green-900/30', border: 'border-green-500', text: 'text-green-600 dark:text-green-400', textDark: 'text-green-700 dark:text-green-300', icon: 'text-green-500', gradient: 'from-green-300 to-green-200 dark:from-green-700 dark:to-green-600' },
      orange: { bg: 'bg-orange-100 dark:bg-orange-900/30', border: 'border-orange-500', text: 'text-orange-600 dark:text-orange-400', textDark: 'text-orange-700 dark:text-orange-300', icon: 'text-orange-500', gradient: 'from-orange-300 to-orange-200 dark:from-orange-700 dark:to-orange-600' },
      purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', border: 'border-purple-500', text: 'text-purple-600 dark:text-purple-400', textDark: 'text-purple-700 dark:text-purple-300', icon: 'text-purple-500', gradient: 'from-purple-300 to-purple-200 dark:from-purple-700 dark:to-purple-600' },
      red: { bg: 'bg-red-100 dark:bg-red-900/30', border: 'border-red-500', text: 'text-red-600 dark:text-red-400', textDark: 'text-red-700 dark:text-red-300', icon: 'text-red-500', gradient: 'from-red-300 to-red-200 dark:from-red-700 dark:to-red-600' },
      yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', border: 'border-yellow-500', text: 'text-yellow-600 dark:text-yellow-400', textDark: 'text-yellow-700 dark:text-yellow-300', icon: 'text-yellow-500', gradient: 'from-yellow-300 to-yellow-200 dark:from-yellow-700 dark:to-yellow-600' },
      gray: { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-500', text: 'text-gray-600 dark:text-gray-400', textDark: 'text-gray-700 dark:text-gray-300', icon: 'text-gray-500', gradient: 'from-gray-300 to-gray-200 dark:from-gray-700 dark:to-gray-600' },
    };
    return colorMap[color] || colorMap.gray;
  };

  return (
    <div className="relative pl-8">
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-green-200 dark:from-blue-800 dark:via-purple-800 dark:to-green-800" />
      <div className="space-y-6">
        {events.map((event, index) => {
          const isLast = index === events.length - 1;
          const StatusIcon = event.config?.icon || getStatusConfig(event.status)?.icon || Package;
          const colorClasses = getColorClasses(event.config?.color || 'gray');
          return (
            <div key={event.id || index} className="relative flex items-start space-x-4">
              <div className="relative z-10 flex-shrink-0 -ml-8">
                <div
                  className={`p-3 rounded-full border-2 ${colorClasses.bg} ${colorClasses.border} ${isLast ? 'ring-4 ring-opacity-50 shadow-lg scale-110' : ''} transition-all duration-200 hover:scale-105`}
                  style={isLast ? { backgroundColor: colorClasses.bg.includes('blue') ? '#3B82F6' : colorClasses.bg.includes('green') ? '#10B981' : colorClasses.bg.includes('red') ? '#EF4444' : colorClasses.bg.includes('purple') ? '#8B5CF6' : colorClasses.bg.includes('orange') ? '#F97316' : '#6B7280' } : {}}
                >
                  <StatusIcon className={`w-5 h-5 ${isLast ? 'text-white' : colorClasses.text}`} />
                </div>
                {!isLast && (
                  <div className={`absolute top-12 left-1/2 transform -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b ${colorClasses.gradient}`} />
                )}
              </div>
              <div className="flex-1 min-w-0 pb-4">
                <div className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border-l-4 ${colorClasses.border} hover:shadow-md transition-shadow`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-white text-base mb-1">{event.description}</p>
                      {event.location && (
                        <div className="flex items-center gap-2 mt-2 mb-2">
                          <MapPin className={`w-4 h-4 ${colorClasses.icon}`} />
                          <p className={`text-sm ${colorClasses.textDark}`}>{event.location}</p>
                        </div>
                      )}
                      {event.details && event.details.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {event.details.map((detail, idx) => (
                            <p key={idx} className="text-xs text-gray-600 dark:text-gray-400">{detail}</p>
                          ))}
                        </div>
                      )}
                      {event.date && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{formatTimelineDate(event.date)}</p>
                        </div>
                      )}
                    </div>
                    {event.eventType && (
                      <Badge variant="outline" className={`text-xs shrink-0 ${colorClasses.border} ${colorClasses.textDark}`}>
                        {event.eventTypeLabel || event.eventType.replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Formate l'objet recipient de l'API pour l'affichage (nom, téléphone, etc.) */
const formatRecipientFromApi = (recipient) => {
  if (!recipient || typeof recipient !== 'object') return null;
  const name = recipient.recipient_name || [recipient.recipient_first_name, recipient.recipient_last_name].filter(Boolean).join(' ').trim() || null;
  const phone = recipient.pickup_phone_number || null;
  const company = recipient.pickup_company_name || null;
  const address = recipient.address || null;
  const email = recipient.email || null;
  if (!name && !phone && !company && !address && !email) return null;
  return { name, phone, company, address, email };
};

/**
 * Bloc de suivi identique à RelayPackages (modal timeline).
 * data: { expedition_number, current_status, current_relay, destination_relay, created_by_relay, code_retrait?, recipient?, events }
 * recipientDisplay: chaîne pour "Destinataire" si data.recipient absent (optionnel, défaut "—")
 * isLoading: boolean
 * onClose: callback pour le bouton Fermer (optionnel)
 * onCopyTracking: (trackingNumber) => Promise<void> pour copier le N°
 */
export function TrackingTimelineBlock({
  data,
  isLoading = false,
  recipientDisplay = '—',
  onClose,
  onCopyTracking,
}) {
  const traceabilityData = data;
  const expeditionNumber = traceabilityData?.expedition_number || '—';
  const currentStatusLabel = getCurrentStatusLabel(traceabilityData?.current_status);
  const recipientFormatted = formatRecipientFromApi(traceabilityData?.recipient);

  let timelineEvents = [];
  let useFallback = false;
  if (traceabilityData?.events && traceabilityData.events.length > 0) {
    timelineEvents = transformApiEventsToTimeline(traceabilityData.events);
  } else if (!isLoading) {
    useFallback = true;
    const uiStatus = mapStatusToUi(traceabilityData?.current_status);
    timelineEvents = generateTimelineEvents(uiStatus, new Date().toISOString(), null, false, '');
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Suivi - {expeditionNumber}
          </h3>
          {traceabilityData?.expedition_number && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Numéro d'expédition: {traceabilityData.expedition_number}
            </p>
          )}
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        )}
      </div>

      {/* Informations générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg mb-6">
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase mb-1">Destinataire</p>
          {recipientFormatted ? (
            <div className="text-gray-900 dark:text-white space-y-0.5">
              {recipientFormatted.name && <p className="font-semibold">{recipientFormatted.name}</p>}
              {recipientFormatted.company && <p className="text-sm">{recipientFormatted.company}</p>}
              {recipientFormatted.phone && <p className="text-sm text-gray-600 dark:text-gray-300">{recipientFormatted.phone}</p>}
              {recipientFormatted.address && <p className="text-sm text-gray-600 dark:text-gray-300">{recipientFormatted.address}</p>}
              {recipientFormatted.email && <p className="text-sm text-gray-600 dark:text-gray-300">{recipientFormatted.email}</p>}
            </div>
          ) : (
            <p className="font-semibold text-gray-900 dark:text-white">{recipientDisplay}</p>
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase mb-1">Statut actuel</p>
          <Badge variant={currentStatusLabel === 'Livré' || currentStatusLabel === 'Retiré' ? 'success' : 'default'} className="mt-1">
            {currentStatusLabel}
          </Badge>
        </div>
      </div>

      {/* Points relais */}
      {traceabilityData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {traceabilityData.created_by_relay && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Point relais créateur</p>
              <p className="font-medium text-gray-900 dark:text-white">{traceabilityData.created_by_relay.nom}</p>
              {traceabilityData.created_by_relay.ville && (
                <p className="text-sm text-gray-600 dark:text-gray-400">{traceabilityData.created_by_relay.ville}</p>
              )}
            </div>
          )}
          {traceabilityData.current_relay && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Point relais actuel</p>
              <p className="font-medium text-blue-900 dark:text-blue-100">{traceabilityData.current_relay.nom}</p>
              {traceabilityData.current_relay.ville && (
                <p className="text-sm text-blue-700 dark:text-blue-300">{traceabilityData.current_relay.ville}</p>
              )}
            </div>
          )}
          {traceabilityData.destination_relay && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Destination finale</p>
              <p className="font-medium text-green-900 dark:text-green-100">{traceabilityData.destination_relay.nom}</p>
              {traceabilityData.destination_relay.ville && (
                <p className="text-sm text-green-700 dark:text-green-300">{traceabilityData.destination_relay.ville}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Code de retrait */}
      {traceabilityData?.code_retrait && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          {/* <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
            Code de retrait: <strong className="text-lg font-mono">{traceabilityData.code_retrait}</strong>
          </p> */}
          {traceabilityData?.destination_relay && (
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium">{traceabilityData.destination_relay.nom}</p>
              <p>{traceabilityData.destination_relay.adresse}</p>
              {traceabilityData.destination_relay.ville && <p>{traceabilityData.destination_relay.ville}</p>}
            </div>
          )}
        </div>
      )}

      {/* Historique de traçabilité */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Suivi de Colis
          </h3>
          {traceabilityData?.events?.length > 0 && (
            <Badge className="text-xs border border-gray-300 dark:border-gray-600">
              {traceabilityData.events.length} événement{traceabilityData.events.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {isLoading ? (
          <TrackingTimeline events={[]} isLoading={true} />
        ) : useFallback ? (
          <>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                ⚠️ Aucune donnée de traçabilité disponible depuis l'API
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                Affichage d'un historique estimé basé sur le statut actuel
              </p>
            </div>
            <TrackingTimeline events={timelineEvents} />
          </>
        ) : (
          <TrackingTimeline events={timelineEvents} />
        )}
      </div>

      {/* Actions rapides (Copier N°) */}
      {(onCopyTracking || onClose) && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Actions rapides</h3>
          <div className="flex flex-wrap gap-2">
            {onCopyTracking && (
              <Button variant="outline" size="sm" onClick={() => onCopyTracking(expeditionNumber)}>
                Copier N°
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackingTimelineBlock;
